/**
 * Voice input helpers — Web Speech API wrapper + parsers.
 *
 * iter-23: free, browser-native voice. No Whisper / LLM cost. Falls back
 * silently to "no mic" when the browser doesn't support webkitSpeechRecognition
 * (older Firefox, some embedded webviews).
 */

const SR =
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

export const VoiceSupported = !!SR;

// iter-23b: Whisper-backed recorder. Works in any browser with MediaRecorder
// (all modern mobile + desktop). More accurate than browser Web Speech API in
// noisy environments. Adds ~$0.006/min cost.
export const RecorderSupported =
  typeof window !== "undefined" &&
  typeof MediaRecorder !== "undefined" &&
  !!navigator.mediaDevices?.getUserMedia;

/**
 * Run a single short voice capture. Resolves with the final transcript string.
 * Auto-stops on silence (browser-controlled) OR when caller calls .abort().
 *
 * @returns {{ promise: Promise<string>, abort: () => void }}
 */
export function listenOnce({ lang = "en-US", interim = false, onInterim } = {}) {
  if (!SR) {
    return {
      promise: Promise.reject(new Error("Speech recognition not supported in this browser")),
      abort: () => {},
    };
  }
  const rec = new SR();
  rec.lang = lang;
  rec.continuous = false;
  rec.interimResults = !!interim;
  rec.maxAlternatives = 1;

  let aborted = false;
  let finalText = "";

  const promise = new Promise((resolve, reject) => {
    rec.onresult = (event) => {
      // Concatenate any final results; emit interim ones via callback if asked
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        const txt = res[0]?.transcript || "";
        if (res.isFinal) finalText += (finalText ? " " : "") + txt;
        else interimText += txt;
      }
      if (interim && onInterim && interimText) onInterim(interimText);
    };
    rec.onerror = (event) => {
      if (event.error === "no-speech" || event.error === "aborted") {
        // Treat silence/abort as graceful empty
        resolve(finalText.trim());
        return;
      }
      reject(new Error(`speech: ${event.error}`));
    };
    rec.onend = () => {
      if (!aborted) resolve(finalText.trim());
    };
    try {
      rec.start();
    } catch (err) {
      reject(err);
    }
  });

  return {
    promise,
    abort: () => {
      aborted = true;
      try { rec.stop(); } catch { /* ignore */ }
    },
  };
}

/**
 * Parse a comma-separated / "and"-separated / pause-separated list of tags.
 * Strips conjunctions, lowercases, dedupes.
 * Example: "portraits, sunset and beach or weddings" → ["portraits", "sunset", "beach", "weddings"]
 */
export function parseTagList(raw) {
  if (!raw) return [];
  // Replace " and ", " or ", semicolons with commas; collapse "comma" word.
  const normalized = raw
    .replace(/\bcomma\b/gi, ",")
    .replace(/\b(?:and|or)\b/gi, ",")
    .replace(/[;|]/g, ",")
    .replace(/\s+/g, " ")
    .trim();
  return normalized
    .split(",")
    .map((t) => t.trim().replace(/[.!?]+$/g, ""))
    .filter(Boolean)
    .filter((t, i, arr) => arr.indexOf(t) === i);
}

/**
 * Smart-route a single utterance into the three Session Setup fields.
 * Returns whatever it could parse — fields not mentioned stay undefined.
 *
 * Recognized cue words (case-insensitive):
 *   "name (is|:)? <text>"               → name
 *   "tags (are|:)? <list>"              → base_tags
 *   "sequence (is|number|:)? <number>"  → start_sequence
 *
 * Without cues, defaults to: everything-before-first-comma = name, rest = tags.
 *
 * Examples:
 *   "Name: Maria's wedding, tags portraits sunset beach, sequence 401"
 *   "Maria's wedding, portraits, sunset, beach"   ← no cues, falls back
 */
export function parseSessionSetupUtterance(raw) {
  if (!raw) return {};
  const text = String(raw).trim();
  const lower = text.toLowerCase();

  const out = {};

  // Cue-based extraction. We anchor each cue's segment to the next cue or end.
  const cues = [
    { key: "name", re: /\bname(?:\s+is)?\s*[:\-]?\s*/i },
    { key: "tags", re: /\b(?:tags?|tagged)(?:\s+are)?\s*[:\-]?\s*/i },
    {
      key: "sequence",
      re: /\b(?:sequence|seq|starting(?:\s+(?:sequence|number))?|number)\s*[:\-]?\s*/i,
    },
  ];

  // Find each cue's index; segment between cue and next-cue (or end).
  const hits = [];
  for (const c of cues) {
    const m = c.re.exec(text);
    if (m) hits.push({ key: c.key, start: m.index, after: m.index + m[0].length });
  }
  if (hits.length > 0) {
    hits.sort((a, b) => a.start - b.start);
    for (let i = 0; i < hits.length; i++) {
      const start = hits[i].after;
      const end = i + 1 < hits.length ? hits[i + 1].start : text.length;
      const segment = text.slice(start, end).replace(/[,.\s]+$/g, "").trim();
      if (!segment) continue;
      if (hits[i].key === "name") out.name = segment;
      else if (hits[i].key === "tags") out.base_tags = parseTagList(segment).join(", ");
      else if (hits[i].key === "sequence") {
        const num = parseInt(segment.replace(/[^\d]/g, ""), 10);
        if (Number.isFinite(num) && num >= 1) out.start_sequence = num;
      }
    }
    return out;
  }

  // No cues — fallback: first comma-separated chunk = name, rest = tags
  const parts = text.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) return {};
  out.name = parts[0];
  if (parts.length > 1) {
    out.base_tags = parseTagList(parts.slice(1).join(",")).join(", ");
  }
  // Stray trailing number → starting sequence
  const trailingNum = /\b(\d{1,6})\s*$/.exec(text);
  if (trailingNum && parts.length > 1) {
    const n = parseInt(trailingNum[1], 10);
    if (Number.isFinite(n) && n >= 1 && n <= 999999) out.start_sequence = n;
    // Also strip from tags
    if (out.base_tags) {
      out.base_tags = out.base_tags
        .split(",").map((s) => s.trim())
        .filter((s) => s !== String(n)).join(", ");
    }
  }
  return out;
}

/**
 * Tiny TTS prompt. Returns a Promise that resolves when speech ends.
 * Silent if the user has voice prompts disabled or the browser lacks speechSynthesis.
 */
export function speak(text, { enabled = true, lang = "en-US" } = {}) {
  if (!enabled || typeof window === "undefined" || !window.speechSynthesis) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = lang;
      u.rate = 1.05;
      u.pitch = 1.0;
      u.onend = () => resolve();
      u.onerror = () => resolve();
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch {
      resolve();
    }
  });
}

/**
 * Record audio from the mic and POST it to /api/voice/transcribe.
 * Resolves with the Whisper transcript string. Better in noisy environments
 * than the browser Web Speech API.
 *
 * @param {Object} opts
 * @param {number} [opts.maxMs=15000]  Hard cap on recording length
 * @param {(level:number)=>void} [opts.onLevel]  Optional audio-level callback (0..1)
 * @param {string} [opts.prompt]  Whisper context hint
 * @param {string} [opts.language]  ISO-639-1 hint
 * @returns {{ promise: Promise<string>, stop: () => void }}
 */
export function recordAndTranscribe({ maxMs = 15000, onLevel, prompt, language } = {}) {
  if (!RecorderSupported) {
    return {
      promise: Promise.reject(new Error("Audio recording not supported in this browser")),
      stop: () => {},
    };
  }
  let stopFn = () => {};
  const promise = (async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const chunks = [];
    const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
    const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);

    let audioCtx, analyser, levelTimer;
    if (onLevel) {
      try {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const src = audioCtx.createMediaStreamSource(stream);
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.frequencyBinCount);
        levelTimer = setInterval(() => {
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) {
            const v = (buf[i] - 128) / 128;
            sum += v * v;
          }
          onLevel(Math.min(1, Math.sqrt(sum / buf.length) * 2));
        }, 100);
      } catch { /* level metering is best-effort */ }
    }

    const done = new Promise((resolve, reject) => {
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
      mr.onerror = (e) => reject(e.error || new Error("MediaRecorder error"));
      mr.onstop = async () => {
        if (levelTimer) clearInterval(levelTimer);
        try { audioCtx?.close(); } catch { /* ignore */ }
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: mimeType || "audio/webm" });
        if (!blob.size) { resolve(""); return; }
        const form = new FormData();
        const ext = (mimeType.includes("webm") ? "webm" : "ogg");
        form.append("audio", blob, `clip.${ext}`);
        if (prompt) form.append("prompt", prompt);
        if (language) form.append("language", language);
        // iter-33 — identity for per-user daily rate-limiting. Grabs the same
        // billingEmail localStorage key the Stripe flow uses. Anonymous
        // callers (no email in storage) fall back to IP-based limiting server-side.
        try {
          const be = window.localStorage.getItem("ssc:billingEmail");
          if (be && be.includes("@")) form.append("email", be);
        } catch { /* localStorage may be disabled — IP fallback still works */ }
        try {
          const base = process.env.REACT_APP_BACKEND_URL;
          const resp = await fetch(`${base}/api/voice/transcribe`, {
            method: "POST",
            body: form,
          });
          if (!resp.ok) {
            // iter-33 — surface the rate-limit case with a clean message.
            if (resp.status === 429) {
              let body = {};
              try { body = await resp.json(); } catch { /* keep body {} */ }
              const err = new Error(
                body?.detail ||
                "Daily voice limit reached. Resets at midnight UTC."
              );
              err.code = "rate_limited";
              err.status = 429;
              err.usage = { count: body?.count, limit: body?.limit,
                            resets_in_seconds: body?.resets_in_seconds };
              reject(err);
              return;
            }
            const txt = await resp.text();
            reject(new Error(`transcribe: ${resp.status} ${txt.slice(0, 120)}`));
            return;
          }
          const data = await resp.json();
          resolve((data?.text || "").trim());
        } catch (err) {
          reject(err);
        }
      };
    });

    mr.start();
    const cap = setTimeout(() => { try { mr.stop(); } catch { /* ignore */ } }, maxMs);
    stopFn = () => { clearTimeout(cap); try { mr.stop(); } catch { /* ignore */ } };
    return done;
  })();

  return { promise, stop: () => stopFn() };
}

